import { ComponentDataSourceService } from '../../component-data-source.service';
import { DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../../environment/environment';
import { EventDTO } from '../../../../dto/event/event.dto';
import { Router } from '@angular/router';
import {
  fetchParticipationCanCancel$,
  fetchParticipationRegisterOpen$,
  PARTICIPATION_REGISTER_CLOSED_FALLBACK
} from './event-participation-register-open.utils';

const EVENT_GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Inverse visibility data source for the event participate form.
 * Returns { visible: boolean } that is true when org setting "events_allow_register_others" is disabled.
 * Intended for a fallback/info page when "add other users" is not allowed.
 * Hidden when registration is closed (participation/register-open), when user can self-cancel (can-cancel),
 * or when already participating (show-catering).
 */
export function event_disallow_register_others(service: ComponentDataSourceService): DataSourceFunction {
  return {
    name: 'event_disallow_register_others',
    description: 'Inverse of event_allow_register_others. Visible when org disallows registering other users (events_allow_register_others is false). For visibilityDataSource.',
    execute: (_params?: LoadDataParams): Observable<{ visible: boolean; message?: string }> => {
      const router = service.injector.get(Router);
      const urlWithoutQuery = (router.url || '').split('?')[0];
      const pathSegments = urlWithoutQuery.split('/');
      const eventGuid = pathSegments.find((segment) => EVENT_GUID_REGEX.test(segment));

      if (!eventGuid) {
        return of({ visible: true });
      }

      return fetchParticipationCanCancel$(service).pipe(
        switchMap((canCancel) => {
          if (canCancel) {
            return of({ visible: false });
          }
          return fetchParticipationRegisterOpen$(service, _params).pipe(
            switchMap(({ open, message }) => {
              if (!open) {
                return of({ visible: false, message: message || PARTICIPATION_REGISTER_CLOSED_FALLBACK });
              }
              return service.http.get<EventDTO>(`${environment.apiUrl}/events/${eventGuid}`).pipe(
                switchMap((eventData: EventDTO) => {
                  const eventAny = eventData as unknown as Record<string, unknown>;
                  const orgId = eventData.organizations_id ?? eventData.organization?.id ?? eventAny['organization_id'];
                  if (orgId == null) {
                    return of({ visible: true });
                  }
                  const obs = service.executeDataSource('org_settings', { organizationId: orgId });
                  if (!obs) {
                    return of({ visible: true });
                  }
                  return obs.pipe(
                    switchMap((res: { data?: Record<string, unknown> }) => {
                      const value = res?.data?.['events_allow_register_others'];
                      const allow = value !== false && value !== 'false' && value !== 0;
                      const disallowRegisterOthers = !allow;

                      return service.http
                        .get<{ visible?: boolean; data?: { visible?: boolean } }>(
                          `${environment.apiUrl}/events/${eventGuid}/participation/show-catering`
                        )
                        .pipe(
                          map((participationRes) => {
                            const notAlreadyParticipating =
                              participationRes?.visible === true || participationRes?.data?.visible === true;
                            return { visible: disallowRegisterOthers && notAlreadyParticipating };
                          }),
                          catchError((err) => {
                            console.warn('[event_disallow_register_others] Error (participation/show-catering):', err?.message || err, '→ visible: true (page will show)');
                            return of({ visible: true });
                          })
                        );
                    })
                  );
                }),
                catchError((err) => {
                  console.warn('[event_disallow_register_others] Error (event or org_settings):', err?.message || err, '→ visible: true (page will show)');
                  return of({ visible: true });
                })
              );
            }),
            catchError((err) => {
              console.warn('[event_disallow_register_others] Error (register-open):', err?.message || err, '→ visible: true (page will show)');
              return of({ visible: true });
            })
          );
        })
      );
    }
  };
}
